import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import cargos from '../../libs/cargos';
import './CadastroPage.css';

// API base URL based on environment
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL || 'https://api.veridicus.com.br'
  : process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:5000';

const CadastroPage = () => {
  const navigate = useNavigate();
  
  // Form fields state
  const [formData, setFormData] = useState({
    razaoSocial: '',
    cnpj: '',
    nire: '',
    nomeFantasia: '',
    telefone: '',
    celularWhatsApp: '',
    telefoneResponsavel: '',
    celularWhatsAppResponsavel: '',
    cep: '',
    endereco: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    pais: 'Brasil',
    email: '',
    emailRF: '',
    password: '',
    confirmPassword: '',
    nomeCompletoResponsavel: '',
    cargo: '',
    dataNascimento: '',
    nomeCompletoAssinanteDBE: '',
    cargoAssinanteDBE: '',
    dataNascimentoAssinanteDBE: ''
  });
  
  // Form submission state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // Countdown state for redirection timer
  const [countdown, setCountdown] = useState(5);

  // States list for dropdown
  const estados = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 
    'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 
    'SP', 'SE', 'TO'
  ];
  
  // Handler for input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };
  
  // Format CNPJ as user types
  const formatCNPJ = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    
    if (value.length > 14) {
      value = value.slice(0, 14);
    }
    
    if (value.length > 12) {
      value = value.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2}).*/, '$1.$2.$3/$4-$5');
    } else if (value.length > 8) {
      value = value.replace(/^(\d{2})(\d{3})(\d{3})(\d*).*/, '$1.$2.$3/$4');
    } else if (value.length > 5) {
      value = value.replace(/^(\d{2})(\d{3})(\d*).*/, '$1.$2.$3');
    } else if (value.length > 2) {
      value = value.replace(/^(\d{2})(\d*).*/, '$1.$2');
    }
    
    setFormData({ ...formData, cnpj: value });
  };
  
  // CNPJ lookup function
  const [cnpjInfo, setCnpjInfo] = useState(null);
  const [loadingCnpj, setLoadingCnpj] = useState(false);
  
  const fetchCNPJInfo = async () => {
    const cnpj = formData.cnpj.replace(/\D/g, ''); // Remove non-digits
    
    if (cnpj.length !== 14) {
      alert('CNPJ deve ter 14 dígitos');
      return;
    }
    
    setLoadingCnpj(true);
    
    try {
      // Make a direct request to the API with the CNPJ
      const response = await fetch(`https://open.cnpja.com/office/${cnpj}`);
      
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
      
      const data = await response.json();
      setCnpjInfo(data);      
    } catch (error) {
      console.error('Error fetching CNPJ info:', error);
      alert('Error fetching CNPJ info: ' + error.message);
      setCnpjInfo(null);
    } finally {
      setLoadingCnpj(false);
    }
  };
  
  // Format phone numbers as user types
  const formatPhone = (e) => {
    const { name } = e.target;
    let value = e.target.value.replace(/\D/g, '');
    
    if (value.length > 11) {
      value = value.slice(0, 11);
    }
    
    if (value.length > 10) {
      value = value.replace(/^(\d{2})(\d{5})(\d{4}).*/, '($1) $2-$3');
    } else if (value.length > 6) {
      value = value.replace(/^(\d{2})(\d{4})(\d*).*/, '($1) $2-$3');
    } else if (value.length > 2) {
      value = value.replace(/^(\d{2})(\d*).*/, '($1) $2');
    }
    
    setFormData({ ...formData, [name]: value });
  };
  
  // Format CEP as user types
  const formatCEP = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    
    if (value.length > 8) {
      value = value.slice(0, 8);
    }
    
    if (value.length > 5) {
      value = value.replace(/^(\d{5})(\d{3}).*/, '$1-$2');
    }
    
    setFormData({ ...formData, cep: value });
  };
  
  // Lookup address by CEP
  const fetchAddressByCEP = async () => {
    const cep = formData.cep.replace(/\D/g, '');
    
    if (cep.length !== 8) return;
    
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      
      if (!data.erro) {
        setFormData({
          ...formData,
          endereco: data.logradouro,
          bairro: data.bairro,
          cidade: data.localidade,
          estado: data.uf
        });
      }
    } catch (error) {
      console.error('Error fetching address:', error);
    }
  };
  
  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não conferem');
      return;
    }
    
    // Required fields validation
    const requiredFields = ['razaoSocial', 'cnpj', 'email', 'password'];
    for (const field of requiredFields) {
      if (!formData[field]) {
        setError(`O campo ${field} é obrigatório`);
        return;
      }
    }
    
    setError('');
    setLoading(true);
    
    // Remove confirmPassword before sending to API
    const clienteData = { ...formData };
    delete clienteData.confirmPassword;
    
    try {     
      // Make API call to register the client
      const response = await fetch(`${API_BASE_URL}/api/clientes/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(clienteData),
      });
      
      // Get response data
      const data = await response.json();      
      if (!response.ok) {
        throw new Error(data.message || 'Erro ao cadastrar');
      }
      
      setSuccess(true);
      setLoading(false);
      
      // Start countdown for login page redirect
      const countdownInterval = setInterval(() => {
        setCountdown(prevCount => {
          if (prevCount <= 1) {
            clearInterval(countdownInterval);
            navigate('/login');
            return 0;
          }
          return prevCount - 1;
        });
      }, 1000);

      // Clear interval if component unmounts
      return () => clearInterval(countdownInterval);
      
    } catch (error) {
      console.error('Erro durante o cadastro:', error);
      setError(error.message || 'Erro ao cadastrar. Tente novamente ou contate o suporte.');
      setLoading(false);
    }
  };
  
  return (
    <>
      <Header />
      
      <section className="cadastro-section">
        <div className="cadastro-container">
          <div className="cadastro-header">
            <h2>Cadastro de Cliente</h2>
            <p>Crie sua conta para acessar nossos serviços</p>
          </div>
          
          {success && (
            <div className="success-message">
              <h3 style={{ marginTop: 0, fontSize: '1.2rem' }}>Cadastro realizado com sucesso!</h3>
              <p>Seu cadastro foi concluído e sua conta foi criada.</p>
              <p>Você será redirecionado para a página de login em <strong>{countdown}</strong> segundos...</p>
              <div style={{ marginTop: '15px' }}>
                <Link to="/login" style={{ color: '#155724', fontWeight: 'bold', textDecoration: 'underline' }}>Clique aqui</Link> para ir agora.
              </div>
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            {/* Dados da Empresa */}
            <div className="form-section">
              <h3>Dados da Empresa</h3>
              
              <div className="form-row">
                <div className="form-group required span-6">
                  <label htmlFor="razaoSocial">Razão Social</label>
                  <input 
                    type="text" 
                    id="razaoSocial"
                    name="razaoSocial"
                    value={formData.razaoSocial}
                    onChange={handleChange}
                    placeholder="Razão Social da empresa"
                    required
                  />
                </div>
                
                <div className="form-group required span-6">
                  <label htmlFor="cnpj">CNPJ - Obrigatório</label>
                  <div className="input-with-button">
                    <input 
                      type="text" 
                      id="cnpj"
                      name="cnpj"
                      value={formData.cnpj}
                      onChange={formatCNPJ}
                      placeholder="00.000.000/0000-00"
                      required
                    />
                    <button 
                      type="button" 
                      onClick={fetchCNPJInfo} 
                      className="search-button"
                    >
                      Buscar
                    </button>
                  </div>
                  {loadingCnpj && <small className="loading-text">Buscando informações...</small>}
                </div>
              </div>
              
              {/* CNPJ Information Display */}
              {cnpjInfo && (
                <div className="form-row">
                  <div className="form-group span-12">
                    <div className="cnpj-info-box">
                      <h4>Informações do CNPJ</h4>
                      <pre>{JSON.stringify(cnpjInfo, null, 2)}</pre>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="form-row">
                <div className="form-group span-6">
                  <label htmlFor="nomeFantasia">Nome Fantasia</label>
                  <input 
                    type="text" 
                    id="nomeFantasia"
                    name="nomeFantasia"
                    value={formData.nomeFantasia}
                    onChange={handleChange}
                    placeholder="Nome Fantasia (opcional)"
                  />
                </div>
                
                <div className="form-group span-6">
                  <label htmlFor="nire">NIRE - Obrigatório</label>
                  <input 
                    type="text" 
                    id="nire"
                    name="nire"
                    value={formData.nire}
                    onChange={handleChange}
                    placeholder="NIRE"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group span-6">
                  <label htmlFor="telefone">Telefone</label>
                  <input 
                    type="text" 
                    id="telefone"
                    name="telefone"
                    value={formData.telefone}
                    onChange={formatPhone}
                    placeholder="(00) 0000-0000"
                  />
                </div>
                
                <div className="form-group span-6">
                  <label htmlFor="celularWhatsApp">Celular / WhatsApp</label>
                  <input 
                    type="text" 
                    id="celularWhatsApp"
                    name="celularWhatsApp"
                    value={formData.celularWhatsApp}
                    onChange={formatPhone}
                    placeholder="(00) 00000-0000"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group span-12">
                  <label htmlFor="emailRF">Email do Responsável</label>
                  <input 
                    type="email" 
                    id="emailRF"
                    name="emailRF"
                    value={formData.emailRF}
                    onChange={handleChange}
                    placeholder="email@responsavel.com.br"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group span-6">
                  <label htmlFor="telefoneResponsavel">Telefone do Responsável</label>
                  <input 
                    type="text" 
                    id="telefoneResponsavel"
                    name="telefoneResponsavel"
                    value={formData.telefoneResponsavel}
                    onChange={formatPhone}
                    placeholder="(00) 0000-0000"
                  />
                </div>
                
                <div className="form-group span-6">
                  <label htmlFor="celularWhatsAppResponsavel">Celular / WhatsApp do Responsável</label>
                  <input 
                    type="text" 
                    id="celularWhatsAppResponsavel"
                    name="celularWhatsAppResponsavel"
                    value={formData.celularWhatsAppResponsavel}
                    onChange={formatPhone}
                    placeholder="(00) 00000-0000"
                  />
                </div>
              </div>
            </div>
            
            {/* Endereço */}
            <div className="form-section">
              <h3>Endereço</h3>
              
              <div className="form-row">
                <div className="form-group span-4">
                  <label htmlFor="cep">CEP</label>
                  <input 
                    type="text" 
                    id="cep"
                    name="cep"
                    value={formData.cep}
                    onChange={formatCEP}
                    onBlur={fetchAddressByCEP}
                    placeholder="00000-000"
                  />
                </div>
                
                <div className="form-group span-8">
                  <label htmlFor="endereco">Endereço</label>
                  <input 
                    type="text" 
                    id="endereco"
                    name="endereco"
                    value={formData.endereco}
                    onChange={handleChange}
                    placeholder="Rua, Avenida, etc."
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group span-2">
                  <label htmlFor="numero">Número</label>
                  <input 
                    type="text" 
                    id="numero"
                    name="numero"
                    value={formData.numero}
                    onChange={handleChange}
                    placeholder="Nº"
                  />
                </div>
                
                <div className="form-group span-4">
                  <label htmlFor="complemento">Complemento</label>
                  <input 
                    type="text" 
                    id="complemento"
                    name="complemento"
                    value={formData.complemento}
                    onChange={handleChange}
                    placeholder="Sala, Andar, etc."
                  />
                </div>
                
                <div className="form-group span-6">
                  <label htmlFor="bairro">Bairro</label>
                  <input 
                    type="text" 
                    id="bairro"
                    name="bairro"
                    value={formData.bairro}
                    onChange={handleChange}
                    placeholder="Bairro"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group span-6">
                  <label htmlFor="cidade">Cidade</label>
                  <input 
                    type="text" 
                    id="cidade"
                    name="cidade"
                    value={formData.cidade}
                    onChange={handleChange}
                    placeholder="Cidade"
                  />
                </div>
                
                <div className="form-group span-3">
                  <label htmlFor="estado">Estado</label>
                  <select 
                    id="estado"
                    name="estado"
                    value={formData.estado}
                    onChange={handleChange}
                  >
                    <option value="">Selecione...</option>
                    {estados.map(estado => (
                      <option key={estado} value={estado}>{estado}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group span-3">
                  <label htmlFor="pais">País</label>
                  <input 
                    type="text" 
                    id="pais"
                    name="pais"
                    value={formData.pais}
                    onChange={handleChange}
                    placeholder="País"
                  />
                </div>
              </div>
            </div>
            
            {/* Responsável por Assinar Capa de Requerimento */}
            <div className="form-section">
              <h3>Responsável por Assinar Capa de Requerimento</h3>
              
              <div className="form-row">
                <div className="form-group span-6">
                  <label htmlFor="nomeCompletoResponsavel">Nome Completo</label>
                  <input 
                    type="text" 
                    id="nomeCompletoResponsavel"
                    name="nomeCompletoResponsavel"
                    value={formData.nomeCompletoResponsavel}
                    onChange={handleChange}
                    placeholder="Nome completo do responsável"
                  />
                </div>
                
                <div className="form-group span-6">
                  <label htmlFor="cargo">Cargo</label>
                  <select 
                    id="cargo"
                    name="cargo"
                    value={formData.cargo}
                    onChange={handleChange}
                  >
                    <option value="">Selecione o cargo...</option>
                    {cargos.map(cargo => (
                      <option key={cargo} value={cargo}>{cargo}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group span-4">
                  <label htmlFor="dataNascimento">Data de Nascimento</label>
                  <input 
                    type="date" 
                    id="dataNascimento"
                    name="dataNascimento"
                    value={formData.dataNascimento}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
            
            {/* Assinante DBE */}
            <div className="form-section">
              <h3>Assinante DBE</h3>
              
              <div className="form-row">
                <div className="form-group span-6">
                  <label htmlFor="nomeCompletoAssinanteDBE">Nome Completo</label>
                  <input 
                    type="text" 
                    id="nomeCompletoAssinanteDBE"
                    name="nomeCompletoAssinanteDBE"
                    value={formData.nomeCompletoAssinanteDBE || ''}
                    onChange={handleChange}
                    placeholder="Nome completo do assinante DBE"
                  />
                </div>
                
                <div className="form-group span-6">
                  <label htmlFor="cargoAssinanteDBE">Cargo</label>
                  <select 
                    id="cargoAssinanteDBE"
                    name="cargoAssinanteDBE"
                    value={formData.cargoAssinanteDBE || ''}
                    onChange={handleChange}
                  >
                    <option value="">Selecione o cargo...</option>
                    {cargos.map(cargo => (
                      <option key={cargo} value={cargo}>{cargo}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group span-4">
                  <label htmlFor="dataNascimentoAssinanteDBE">Data de Nascimento</label>
                  <input 
                    type="date" 
                    id="dataNascimentoAssinanteDBE"
                    name="dataNascimentoAssinanteDBE"
                    value={formData.dataNascimentoAssinanteDBE || ''}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
            
            {/* Informações de Acesso */}
            <div className="form-section">
              <h3>Informações de Acesso</h3>
              
              <div className="form-row">
                <div className="form-group required span-12">
                  <label htmlFor="email">Email de Login - Obrigatório</label>
                  <input 
                    type="email" 
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="email@empresa.com.br"
                    required
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group required span-6">
                  <label htmlFor="password">Senha - Obrigatório</label>
                  <input 
                    type="password" 
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Senha"
                    required
                  />
                </div>
                
                <div className="form-group required span-6">
                  <label htmlFor="confirmPassword">Confirmar Senha - Obrigatório</label>
                  <input 
                    type="password" 
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirme sua senha"
                    required
                  />
                </div>
              </div>
            </div>
            
            {error && <div className="error-message">{error}</div>}
            
            <button 
              type="submit" 
              className="submit-button" 
              disabled={loading}
            >
              {loading ? 'Cadastrando...' : 'Cadastrar'}
            </button>
          </form>
          
          <div className="login-link">
            <p>Já tem uma conta? <Link to="/login">Faça login</Link></p>
          </div>
        </div>
      </section>
      
      <Footer />
    </>
  );
};

export default CadastroPage;
